import React from 'react';
import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  Image,
  Font,
} from '@react-pdf/renderer';
import { normalizeCtcToLpa } from 'src/lib/utils';
import logoImg from '../../assets/images/ahedge_blue_logo.png';

// Register Roboto font
Font.register({
  family: 'Roboto',
  fonts: [
    {
      src: 'https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-regular-webfont.ttf',
    },
    {
      src: 'https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-medium-webfont.ttf',
      fontWeight: 500,
    },
    {
      src: 'https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-bold-webfont.ttf',
      fontWeight: 700,
    },
  ],
});

const styles = StyleSheet.create({
  page: {
    fontFamily: 'Roboto',
    color: '#333',
    padding: 40,
    position: 'relative',
  },
  container: {
    flex: 1,
    padding: 15,
    position: 'relative',
    zIndex: 1,
    display: 'flex',
    flexDirection: 'column',
  },
  contentContainer: {
    flex: 1,
  },
  watermark: {
    position: 'absolute',
    top: '30%',
    left: '25%',
    right: '25%',
    transform: 'translate(-50%, -50%)',
    opacity: 0.05,
    width: '55%',
    height: '55%',
    zIndex: 0,
  },
  watermarkImage: {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    borderBottom: '1 solid #eee',
    paddingBottom: 10,
    marginBottom: 15,
    position: 'relative',
    zIndex: 1,
  },
  creditNoteInfo: {
    flex: 1,
  },
  creditNoteTitle: {
    fontSize: 22,
    fontWeight: 700,
    color: '#333',
    marginBottom: 6,
  },
  creditNoteNumber: {
    fontSize: 14,
    fontWeight: 500,
    color: '#555',
    marginBottom: 4,
  },
  creditNoteDate: {
    fontSize: 12,
    color: '#555',
    marginBottom: 2,
  },
  poNumber: {
    fontSize: 12,
    color: '#555',
    marginBottom: 5,
  },
  logo: {
    width: 120,
    height: 'auto',
    marginBottom: 8,
  },
  statusBadge: {
    marginTop: 5,
    fontSize: 11,
    fontWeight: 500,
    color: '#fff',
    backgroundColor: '#145EA9',
    padding: '4 8',
    borderRadius: 4,
    alignSelf: 'flex-start',
  },
  section: {
    marginBottom: 20,
  },
  sectionTitle: {
    fontSize: 14,
    color: '#145EA9',
    borderBottom: '2 solid #145EA9',
    paddingBottom: 5,
    marginBottom: 12,
    fontWeight: 500,
  },
  addressContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 20,
  },
  addressBox: {
    width: '48%',
  },
  addressHeading: {
    fontSize: 12,
    fontWeight: 500,
    color: '#555',
    marginBottom: 5,
  },
  companyName: {
    fontSize: 12,
    fontWeight: 700,
    color: '#333',
    marginBottom: 4,
  },
  addressLine: {
    fontSize: 11,
    color: '#555',
    marginBottom: 2,
  },
  gstinLine: {
    fontSize: 11,
    fontWeight: 500,
    color: '#555',
    marginTop: 4,
  },
  creditNoteDetails: {
    backgroundColor: '#f8fafc',
    border: '1 solid #e2e8f0',
    borderRadius: 4,
    padding: 12,
    marginBottom: 20,
  },
  detailRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 6,
  },
  detailLabel: {
    fontSize: 11,
    fontWeight: 500,
    color: '#475569',
  },
  detailValue: {
    fontSize: 11,
    color: '#1e293b',
    fontWeight: 600,
  },
  table: {
    width: '100%',
    marginVertical: 8,
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: '#f9fafb',
    padding: '10 5',
    borderBottom: '1 solid #e5e7eb',
  },
  tableRow: {
    flexDirection: 'row',
    borderBottom: '1 solid #e5e7eb',
    padding: '10 5',
    minHeight: 35,
    alignItems: 'center',
  },
  candidateCell: {
    flex: 2,
    paddingRight: 8,
  },
  requirementCell: {
    flex: 2,
    paddingRight: 8,
  },
  invoiceCell: {
    flex: 2,
    paddingRight: 8,
  },
  amountCell: {
    flex: 1,
    textAlign: 'right',
    paddingRight: 5,
  },
  headerText: {
    fontSize: 10,
    color: '#6b7280',
    fontWeight: 500,
  },
  cellText: {
    fontSize: 10,
    color: '#374151',
  },
  summaryRow: {
    flexDirection: 'row',
    padding: '10 5',
    minHeight: 35,
    alignItems: 'center',
  },
  summaryLabelCell: {
    flex: 7,
    textAlign: 'right',
    paddingRight: 10,
    fontWeight: 500,
    fontSize: 11,
    color: '#555',
  },
  summaryValueCell: {
    flex: 2,
    textAlign: 'right',
    fontSize: 11,
    color: '#374151',
  },
  grandTotalRow: {
    flexDirection: 'row',
    padding: '10 5',
    minHeight: 35,
    alignItems: 'center',
    backgroundColor: '#f9fafb',
  },
  grandTotalLabel: {
    flex: 7,
    textAlign: 'right',
    paddingRight: 10,
    fontWeight: 700,
    fontSize: 12,
    color: '#333',
  },
  grandTotalValue: {
    flex: 2,
    textAlign: 'right',
    fontWeight: 700,
    fontSize: 12,
    color: '#333',
  },
  footer: {
    borderTop: '1 solid #eee',
    paddingTop: 10,
    fontSize: 9,
    color: '#6b7280',
    textAlign: 'center',
    marginTop: 'auto',
  },
});

// Same DRAFT/ISSUED/APPROVED vocabulary and colors as CreditNotesInfo.jsx's
// own StatusTag — this is a credit note's status, not an invoice's, so
// PAID/SENT/OVERDUE (copied from InvoicePDF.jsx originally) never matched.
const getStatusStyle = (status) => {
  switch (status?.toUpperCase()) {
    case 'DRAFT':
      return { backgroundColor: '#1d4ed8' };
    case 'ISSUED':
      return { backgroundColor: '#b45309' };
    case 'APPROVED':
      return { backgroundColor: '#059669' };
    default:
      return { backgroundColor: '#145EA9' };
  }
};

// "Rs." / $ + 2dp — NOT the ₹ glyph. The Roboto TTF pulled in via Font.register
// above is a trimmed "webfont" subset that doesn't carry the ₹ (U+20B9) glyph,
// so react-pdf fell back to a zero-width/missing glyph that visually
// overlapped the digit right after it (the "overlapping first digit" bug).
// "Rs." is plain ASCII and renders correctly in every font. The on-screen
// page isn't affected — browsers have the glyph — so only this PDF-only
// helper needs the workaround.
const formatMoney = (currencyCode, value) =>
  value == null
    ? '—'
    : `${currencyCode === 'INR' ? 'Rs.' : '$'} ${value.toLocaleString('en-IN', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;

// Plain 2dp number, no "Rs." — for the Candidate Details table's CTC/Amount
// cells, where the currency now lives in the column header ("CTC (Rs.
// LPA)" / "Amount (Rs.)") instead of repeating on every row.
const formatNumber = (value, fractionDigits = 2) =>
  value == null
    ? '—'
    : value.toLocaleString('en-IN', {
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits,
      });

// Reusable Page component with footer at the bottom
const PageWithFooter = ({ children }) => (
  <Page size="A4" style={styles.page}>
    <View style={styles.watermark}>
      <Image src={logoImg} style={styles.watermarkImage} />
    </View>
    <View style={styles.container}>
      <View style={styles.contentContainer}>{children}</View>
      <View style={styles.footer}>
        <Text>
          This is a system-generated credit note. No signature required.
        </Text>
      </View>
    </View>
  </Page>
);

const CreditNotesPDF = ({ data }) => {
  // Extract data for easy access
  const {
    creditNoteNumber,
    creditNoteDate,
    poNumber,
    client,
    ahedge,
    creditNoteDetails,
    candidateDetails,
    invoiceNumbers,
    meta,
  } = data;

  // transformApiResponse (CreditNotesInfo.jsx) nests status under meta, not
  // creditNoteDetails — creditNoteDetails?.status was always undefined,
  // which is why the badge never showed a real status.
  const statusStyle = getStatusStyle(meta?.status);
  const currencyCode = creditNoteDetails?.currency === 'INR' ? 'INR' : 'USD';
  // candidate_details comes back as {} rather than omitted when there isn't
  // one (see CreditNotesInfo.jsx's own hasCandidate) — candidate_name is the
  // field confirmed to actually be present, so key off that instead of the
  // object's own truthiness (an empty object is still truthy).
  const hasCandidate = !!candidateDetails?.candidate_name;
  const hasPoNumber = !!poNumber && poNumber !== 'N/A';

  return (
    <Document>
      {/* First page - Main content */}
      <PageWithFooter>
        {/* Header Section */}
        <View style={styles.header}>
          <View style={styles.creditNoteInfo}>
            <Text style={styles.creditNoteTitle}>CREDIT NOTE</Text>
            <Text style={styles.creditNoteNumber}>
              Credit Note Number: {creditNoteNumber}
            </Text>
            <Text style={styles.creditNoteDate}>
              Issue Date: {creditNoteDate}
            </Text>
            {hasPoNumber && (
              <Text style={styles.poNumber}>PO Number: {poNumber}</Text>
            )}
            <View style={[styles.statusBadge, statusStyle]}>
              <Text style={{ color: '#fff', fontSize: 10 }}>
                {meta?.status}
              </Text>
            </View>
          </View>
          <Image src={logoImg} style={styles.logo} />
        </View>

        {/* Address Section */}
        <View style={styles.addressContainer}>
          {/* From Address */}
          <View style={styles.addressBox}>
            <Text style={styles.addressHeading}>From:</Text>
            <Text style={styles.companyName}>{ahedge.name}</Text>
            <Text style={styles.addressLine}>{ahedge.address}</Text>
            <Text style={styles.addressLine}>{ahedge.cityStatePin}</Text>
            <Text style={styles.gstinLine}>GSTIN: {ahedge.gstin}</Text>
          </View>

          {/* To Address */}
          <View style={styles.addressBox}>
            <Text style={styles.addressHeading}>To:</Text>
            <Text style={styles.companyName}>{client.name}</Text>
            {client.address && (
              <>
                {client.address.line1 && (
                  <Text style={styles.addressLine}>{client.address.line1}</Text>
                )}
                {client.address.street && (
                  <Text style={styles.addressLine}>
                    {client.address.street}
                  </Text>
                )}
                {client.address.zipCode && (
                  <Text style={styles.addressLine}>
                    {client.address.zipCode}
                  </Text>
                )}
              </>
            )}
            {client.gstin && client.gstin !== 'N/A' && (
              <Text style={styles.gstinLine}>GSTIN: {client.gstin}</Text>
            )}
          </View>
        </View>

        {/* Credit Note Details Section */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Credit Note Details</Text>
          <View style={styles.creditNoteDetails}>
            <View style={styles.detailRow}>
              <Text style={styles.detailLabel}>Reason:</Text>
              <Text style={styles.detailValue}>
                {creditNoteDetails.creditNoteType}
              </Text>
            </View>
            {creditNoteDetails.details && (
              <View style={styles.detailRow}>
                <Text style={styles.detailLabel}>Details:</Text>
                <Text style={styles.detailValue}>
                  {creditNoteDetails.details}
                </Text>
              </View>
            )}
            {invoiceNumbers?.length > 0 && (
              <View style={styles.detailRow}>
                <Text style={styles.detailLabel}>
                  Invoice{invoiceNumbers.length > 1 ? 's' : ''}:
                </Text>
                <Text style={styles.detailValue}>
                  {invoiceNumbers.join(', ')}
                </Text>
              </View>
            )}
            <View style={styles.detailRow}>
              <Text style={styles.detailLabel}>Amount:</Text>
              <Text style={styles.detailValue}>
                {formatMoney(currencyCode, creditNoteDetails.subTotal)}
              </Text>
            </View>
            <View style={styles.detailRow}>
              <Text style={styles.detailLabel}>
                GST ({creditNoteDetails.taxPercentage}%):
              </Text>
              <Text style={styles.detailValue}>
                {formatMoney(currencyCode, creditNoteDetails.taxAmount)}
              </Text>
            </View>
            <View style={styles.detailRow}>
              <Text style={styles.detailLabel}>Grand Total:</Text>
              <Text style={styles.detailValue}>
                {formatMoney(currencyCode, creditNoteDetails.totalAmount)}
              </Text>
            </View>
          </View>
        </View>

        {/* Candidate Details Section — only when there's a candidate
            attached; a credit note can be posted against just a customer
            (see CreateCreditNoteModal.jsx), in which case the Reason/
            Details above already cover it and this table has nothing
            meaningful to show. */}
        {hasCandidate && (
          <View style={styles.section} wrap={false}>
            <Text style={styles.sectionTitle}>Candidate Details</Text>
            <View style={styles.table}>
              <View style={styles.tableHeader}>
                <Text style={[styles.headerText, styles.candidateCell]}>
                  Candidate Name
                </Text>
                <Text style={[styles.headerText, styles.requirementCell]}>
                  Requirement
                </Text>
                <Text style={[styles.headerText, styles.invoiceCell]}>
                  Invoice Number
                </Text>
                <Text style={[styles.headerText, styles.amountCell]}>
                  CTC (Rs. LPA)
                </Text>
                <Text style={[styles.headerText, styles.amountCell]}>
                  Amount (Rs.)
                </Text>
              </View>
              <View style={styles.tableRow}>
                <Text style={[styles.cellText, styles.candidateCell]}>
                  {candidateDetails.candidate_name || 'N/A'}
                </Text>
                <Text style={[styles.cellText, styles.requirementCell]}>
                  {candidateDetails.requirement_title || 'N/A'}
                </Text>
                <Text style={[styles.cellText, styles.invoiceCell]}>
                  {invoiceNumbers?.length > 0
                    ? invoiceNumbers.join(', ')
                    : 'N/A'}
                </Text>
                <Text style={[styles.cellText, styles.amountCell]}>
                  {formatNumber(
                    normalizeCtcToLpa(candidateDetails.ctc_amount),
                    1
                  )}
                </Text>
                <Text style={[styles.cellText, styles.amountCell]}>
                  {formatNumber(creditNoteDetails.subTotal)}
                </Text>
              </View>
            </View>
          </View>
        )}
      </PageWithFooter>
    </Document>
  );
};

export default CreditNotesPDF;
